import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { Portal } from 'react-portal';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { Container, Segment } from 'semantic-ui-react';
import { defineMessages, injectIntl } from 'react-intl';
import Error from '@plone/volto/components/theme/Error/Error';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import backSVG from '@plone/volto/icons/back.svg';
import {
  createRezeptkategorie,
  getRezeptkategorien,
  getVistPermissions,
} from '../../../actions';
import {
  hasManagementUiAccess,
  selectPermissions,
} from '../../../helpers/permissions';
import ButtonArea from '../ButtonArea';

import RezeptkategorieForm from './RezeptkategorieForm';

const messages = defineMessages({
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
});

const RezeptkategorieCreate = ({ intl }) => {
  const [formData, setFormData] = useState({
    name: '',
    pid: 0,
    id: undefined,
    level: 0,
  });
  const dispatch = useDispatch();
  const [error, setError] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);
  const [isClient, setIsClient] = useState(false);
  const [isRezeptkategorieNameDuplicate, setIsRezeptkategorieNameDuplicate] =
    useState(false);

  const isSubmitDisabled = formData.name === '';

  const history = useHistory();
  // flag to make sure permissions are fully loaded before returning
  // an unauthorized error
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  const pathname = useSelector((state) => state.router.location.pathname);
  const rezeptkategorien = useSelector(
    (state) => state.rezeptkategorien.data.rezeptkategorien,
  );
  const token = useSelector((state) => state.userSession.token);
  const permissions = useSelector(selectPermissions);
  // Management-table CRUD: Admin + F&E per TASK-90 §2.
  const canCreate = hasManagementUiAccess(permissions);

  // load permissions
  useEffect(() => {
    setIsClient(true);
    // Fetch @vist_permissions if the reducer hasn't seen a response yet.
    if (!permissions && permissionsLoading) {
      dispatch(getVistPermissions())
        .then(() => {
          setPermissionsLoading(false);
        })
        .catch(() => {
          setError({ status: 401 });
          setPermissionsLoading(false);
        });
    }
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    setIsClient(true);
    dispatch(getRezeptkategorien({ getAll: true }));
  }, [dispatch]);

  // check for duplicates!
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));

    if (name === 'name') {
      const isDuplicate = rezeptkategorien.some(
        (rezeptkategorie) => rezeptkategorie.name === value,
      );
      setIsRezeptkategorieNameDuplicate(isDuplicate);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Remove fields with empty strings from the formData
    const cleanedFormData = Object.entries(formData).reduce(
      (acc, [key, value]) => {
        if (value !== '') {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );

    dispatch(createRezeptkategorie(cleanedFormData))
      .then((result) => {
        const id = result.id;
        if (id) {
          history.push(`/controlpanel/rezeptdatenbank/rezeptkategorien`);
        } else {
          setError(
            'Es ist etwas schiefgelaufen, Rezeptkategorie wurde nicht angelegt.',
          );
        }
      })
      .catch((error) => {
        setError(
          'Es ist etwas schiefgelaufen, Rezeptkategorie wurde nicht angelegt',
        );
        setErrorDetail(error.name + ': ' + error.message);
      });
  };
  if (!token) {
    return <Error error={{ status: 401 }} />;
  }
  if (!permissionsLoading && !canCreate) {
    return <Error error={{ status: 401 }} />;
  }
  return (
    <div className="view-wrapper rbd add">
      <Helmet title="Rezeptkategorie Anlegen" />
      <Container className="controlpanel">
        <Segment.Group raised>
          <Segment className="primary">
            <div className="rezeptdatenbank header">
              <div className="header-container">
                <h1>Rezeptkategorie Anlegen</h1>
              </div>
              <div className="button-container">
                <ButtonArea
                  isAdd={true}
                  handleSubmit={handleSubmit}
                  isSubmitDisabled={isSubmitDisabled}
                  backURL={'/controlpanel/rezeptdatenbank/rezeptkategorien'}
                  typeString={'die Rezeptgruppe'}
                />
              </div>
            </div>
            <div className="rezeptdatenbank header"></div>
          </Segment>
          <Segment className="rdbAddEditForm">
            {error && (
              <div className="error-message">
                <h2>{error}</h2>
                <p>{errorDetail}</p>
              </div>
            )}
            <div className="rdb add form rezeptkategorien">
              <RezeptkategorieForm
                isRezeptkategorieNameDuplicate={isRezeptkategorieNameDuplicate}
                formData={formData}
                handleInputChange={handleInputChange}
                kategorien={rezeptkategorien}
              />
            </div>
          </Segment>
        </Segment.Group>
      </Container>
      {isClient && (
        <Portal node={document.getElementById('toolbar')}>
          <Toolbar
            pathname={pathname}
            hideDefaultViewButtons
            inner={
              <Link to="/controlpanel/rezeptdatenbank" className="item">
                <Icon
                  name={backSVG}
                  className="contents circled"
                  size="30px"
                  title={intl.formatMessage(messages.back)}
                />
              </Link>
            }
          />
        </Portal>
      )}
    </div>
  );
};

RezeptkategorieCreate.propTypes = {
  intl: PropTypes.object.isRequired,
};

export default injectIntl(RezeptkategorieCreate);
